import { useCallback, useEffect, useState } from 'react';
import {
  Outlet,
  useMatch,
  useNavigate,
  useParams,
  generatePath,
  NavLink,
  useLocation,
} from 'react-router-dom';
import {
  Tabs,
  PermissionEnum,
  PermissionDenied,
  IconArrowLeft,
  getResponseErrors,
  ResponseError,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetPspAccountsCategoriesQuery,
  useLazyGetPspPartnerQuery,
} from './store/services';
import { setBankId } from './store/slices/info';

import { BankRoutes } from '../../routes';

import cn from './bank-and-psp.module.css';

const Info = () => {
  const [listState, setListState] = useState('');
  const [errorList, setErrorList] = useState('');

  const { bankId } = useParams();
  const { state } = useLocation();

  const navigate = useNavigate();
  const match = useMatch(BankRoutes.BANK_AND_PSP_PAGE);
  const dispatch = useAppDispatch();

  const [getPartner, { isLoading, isFetching }] = useLazyGetPspPartnerQuery();
  const [getPspAccountsCategories] = useLazyGetPspAccountsCategoriesQuery();

  const { generalInfo } = useAppSelector(
    (storeState) => storeState.bankAndPsp.info,
  );

  const { activePermissions } = useAppSelector(
    (storeState) => storeState.configurationDictionaries,
  );
  const { pspAccountsCategories } = useAppSelector(
    (storeState) => storeState.bankAndPsp.info,
  );

  const getPartnerAction = useCallback(async () => {
    const response = (await getPartner(bankId as string)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      getResponseErrors({
        error: response.error,
        setErrorList,
      });
    }
  }, [bankId]);

  useEffect(() => {
    if (state && state.search) {
      setListState(state.search);
    }
  }, [state]);

  useEffect(() => {
    if (
      !pspAccountsCategories &&
      activePermissions?.includes(PermissionEnum.PSP_LIST)
    ) {
      getPspAccountsCategories();
    }
  }, [activePermissions, pspAccountsCategories]);

  useEffect(() => {
    if (bankId) {
      dispatch(setBankId(bankId));
    }
  }, [bankId]);

  useEffect(() => {
    if (
      match &&
      bankId &&
      activePermissions?.includes(PermissionEnum.PSP_LIST)
    ) {
      navigate(generatePath(BankRoutes.BANK_AND_PSP_GENERAL, { bankId }));
    }
  }, [bankId, match, activePermissions]);

  useEffect(() => {
    if (bankId && activePermissions?.includes(PermissionEnum.PSP_LIST)) {
      getPartnerAction();
    }
  }, [getPartnerAction, bankId]);

  let content;

  if (!activePermissions?.includes(PermissionEnum.PSP_LIST)) {
    content = <PermissionDenied />;
  } else {
    content = (
      <Tabs
        wrapperClassName={cn.tabs}
        onRouterClick={() => localStorage.removeItem('responseTime')}
        tabs={[
          {
            name: 'General',
            path: generatePath(BankRoutes.BANK_AND_PSP_GENERAL, {
              bankId: bankId as string,
            }),
          },
          {
            name: 'Accounts',
            path: generatePath(BankRoutes.BANK_AND_PSP_ACCOUNTS, {
              bankId: bankId as string,
            }),
          },
          {
            name: 'External Accounts',
            path: generatePath(BankRoutes.BANK_AND_PSP_EXTERNAL, {
              bankId: bankId as string,
            }),
            hidden: !activePermissions.includes(
              PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
            ),
          },
          {
            name: 'Linked Customers',
            path: generatePath(BankRoutes.BANK_AND_PSP_LINKED_CUSTOMERS, {
              bankId: bankId as string,
            }),
            hidden:
              !activePermissions.includes(
                PermissionEnum.EXTERNAL_ORGANIZATIONS_LIST,
              ) ||
              !generalInfo?.pspIntegrationId ||
              (generalInfo?.pspIntegration &&
                !generalInfo?.pspIntegration?.externalCustomersSupported),
          },
          {
            name: 'Integration type',
            path: generatePath(BankRoutes.BANK_AND_PSP_INTEGRATION, {
              bankId: bankId as string,
            }),
          },
          {
            name: 'Integration log',
            path: generatePath(BankRoutes.BANK_AND_PSP_INTEGRATION_LOGS, {
              bankId: bankId as string,
            }),
            hidden: !activePermissions.includes(
              PermissionEnum.PSP_INTEGRATION_LOGS_LIST_BY_PSP_PARTNER,
            ),
          },
        ]}
        enableRouting
      />
    );
  }

  return (
    <div className={cn.bankAndPspPage}>
      <div className={cn.bankAndPspPageContent}>
        <div className={cn.bankAndPspPageLinkWrapper}>
          <NavLink
            to={`${BankRoutes.BANK_AND_PSP_LIST}${listState || ''}`}
            className={cn.bankAndPspPageBackLink}
            onClick={() => {
              localStorage.removeItem('responseTime');
            }}
          >
            <IconArrowLeft width={13} height={11} />
            Back
          </NavLink>
        </div>
        {content}
        <Outlet context={{ errorList, isLoading, isFetching }} />
      </div>
    </div>
  );
};

export default Info;
